import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * Data layer for the FatorH "Sincronizar" drawer (recruiter job-flow).
 *
 * Backs the manual/bulk push of a vaga's subscribers onto its FatorH exams —
 * covering people who subscribed BEFORE the FatorH module existed, or whose
 * automatic subscribe-push silently missed. Idempotent on the FatorH side
 * (find-or-create), so re-pushing anyone is safe.
 *
 * Calls the BFF's `/jobs/:id/fator-h/{candidates,push}` proxies directly via
 * the SDK `fetchClient` (paths not yet in the generated typed SDK — mirrors the
 * catalogues-query approach). Types are declared locally to match Core's shape.
 */

export interface FatorHSyncCandidate {
  userId: string;
  kcUserId: string | null;
  name: string | null;
  email: string | null;
  /** Já empurrado pro workspace FatorH da empresa da vaga. */
  pushed: boolean;
  /** Concluiu o módulo FatorH desta vaga (null = não respondeu). */
  answeredAt: string | null;
  score: number | null;
}

/** Estado da integração da vaga (spec 068/AA-48) — alimenta banner e badge. */
export interface FatorHIntegrationState {
  workspaceBound: boolean;
  hasKey: boolean;
  examMapped: boolean;
}

export interface CandidatesResponse {
  candidates: FatorHSyncCandidate[];
  /** Optional: tolera BFF/Core anteriores durante o rollout. */
  integration?: FatorHIntegrationState;
}

export interface FatorHPushResult {
  pushed: string[];
  skipped: string[];
  failed: { userId: string; error: string }[];
}

export class FatorHSyncError extends Error {
  readonly name = 'FatorHSyncError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export const fatorHSyncQueryKey = (jobId: string) => ['recruiter', 'fator-h-sync', jobId] as const;

/**
 * Traduz o `failed[].error` do Core (422s propositais) para o vocabulário do
 * consultor — o dado que falta é corrigível no cadastro do candidato.
 */
export function friendlyPushError(error: string): string {
  if (/no usable phone/i.test(error)) return 'Sem telefone com DDD no cadastro';
  if (/no usable document/i.test(error)) return 'Sem CPF válido (ou passaporte) no cadastro';
  if (/workspace|api_key|apiKey/i.test(error)) return 'Integração FatorH da empresa incompleta';
  return error.length > 120 ? `${error.slice(0, 117)}…` : error;
}

/** Lista de inscritos da vaga + estado FatorH (push/score) — alimenta o drawer. */
export function fatorHSyncCandidatesQueryOptions(jobId: string, enabled = true) {
  return queryOptions({
    queryKey: fatorHSyncQueryKey(jobId),
    enabled: enabled && jobId.length > 0,
    queryFn: async (): Promise<CandidatesResponse> => {
      const res = await fetchClient<CandidatesResponse>({
        method: 'GET',
        url: `/jobs/${jobId}/fator-h/candidates`,
      });
      if (res.error) {
        throw new FatorHSyncError(
          `Falha ao carregar candidatos FatorH (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    // Fresh-ish: push state changes as the recruiter acts, so re-fetch on reopen.
    staleTime: 30 * 1000,
  });
}

// Core pushes each candidate sequentially (one FatorH round-trip each), so a
// big batch in a single POST risks a gateway timeout that partially applies —
// exactly how a candidate gets silently missed. Keep batches small so each POST
// stays well under the request budget; "enviar todos" runs them in sequence.
const PUSH_BATCH_SIZE = 15;

/**
 * Push idempotente dos `userIds` em lotes pequenos, agregando o resultado.
 * Resiliente por-lote: um lote que estoura (timeout/erro) NÃO aborta os demais —
 * seus ids caem em `failed` (retryáveis por linha) e o loop continua.
 * `onProgress(done, total)` é chamado após cada lote (barra de progresso).
 */
export async function pushFatorHCandidates(
  jobId: string,
  userIds: string[],
  onProgress?: (done: number, total: number) => void
): Promise<FatorHPushResult> {
  const agg: FatorHPushResult = { pushed: [], skipped: [], failed: [] };
  const total = userIds.length;
  for (let i = 0; i < total; i += PUSH_BATCH_SIZE) {
    const batch = userIds.slice(i, i + PUSH_BATCH_SIZE);
    try {
      const res = await fetchClient<FatorHPushResult>({
        method: 'POST',
        url: `/jobs/${jobId}/fator-h/push`,
        data: { userIds: batch },
      });
      if (res.error) {
        for (const id of batch) agg.failed.push({ userId: id, error: `HTTP ${res.error.status}` });
      } else {
        agg.pushed.push(...res.data.pushed);
        agg.skipped.push(...res.data.skipped);
        agg.failed.push(...res.data.failed);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'erro de rede';
      for (const id of batch) agg.failed.push({ userId: id, error: msg });
    }
    onProgress?.(Math.min(i + PUSH_BATCH_SIZE, total), total);
  }
  return agg;
}
